import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Headphones, ExternalLink, ShoppingBag, Mail, UserPlus, FileText } from "lucide-react";
import { JoinDialog } from "./JoinDialog";
import { useShowreel } from "./Showreel";

// Icon per action type
const ICONS = {
  form: UserPlus, internal: ArrowRight, external: ExternalLink,
  video: Play, podcast: Headphones, contact: Mail, product: ShoppingBag,
};

// Available action types (shared with the admin editor)
export const ACTION_TYPES = [
  { value: "form", label: "Formulaire (inscription / candidature / participation)" },
  { value: "internal", label: "Page interne du site" },
  { value: "external", label: "Lien externe" },
  { value: "video", label: "Vidéo (lecteur)" },
  { value: "podcast", label: "Podcast (lien)" },
  { value: "contact", label: "Nous contacter" },
  { value: "product", label: "Commande / Produit (lien)" },
];

const primaryCls = "group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors";
const secondaryCls = "group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-surface text-white border border-white/15 text-[12px] tracking-[0.12em] uppercase font-medium hover:border-electric hover:text-electric transition-colors";

// Generic, fully admin-configurable action buttons for any content item.
// `context` = { refId, refTitle } used to attach form submissions to the right content.
export const ActionButtons = ({ actions, context = {}, className = "" }) => {
  const navigate = useNavigate();
  const showreel = useShowreel();
  const [formAction, setFormAction] = useState(null);

  const list = (actions || []).filter((a) => a && a.label && (a.enabled !== false));
  if (list.length === 0) return null;

  const handle = (a) => {
    switch (a.type) {
      case "form": setFormAction(a); break;
      case "internal": if (a.href) navigate(a.href.startsWith("/") ? a.href : `/${a.href}`); break;
      case "external":
      case "product":
      case "podcast": if (a.url) window.open(a.url, "_blank", "noopener"); break;
      case "video":
        if (a.url && showreel?.open) showreel.open(a.url, context.refTitle || a.label, "");
        else if (a.url) window.open(a.url, "_blank", "noopener");
        break;
      case "contact": navigate("/#contact"); break;
      default: break;
    }
  };

  return (
    <div className={`flex flex-wrap gap-3 ${className}`} data-testid="action-buttons">
      {list.map((a, i) => {
        const Icon = ICONS[a.type] || ArrowRight;
        const cls = (a.style === "secondary") ? secondaryCls : primaryCls;
        return (
          <button key={a.id || i} type="button" onClick={() => handle(a)} data-testid={`action-btn-${i}`} className={cls}>
            {a.label} <Icon size={15} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        );
      })}
      <JoinDialog
        open={!!formAction}
        onClose={() => setFormAction(null)}
        type={formAction?.submissionType || "contact"}
        refId={context.refId || ""}
        refTitle={context.refTitle || ""}
        roles={formAction?.roles || []}
        heading={formAction?.formHeading || formAction?.label}
        intro={formAction?.formIntro || ""}
      />
    </div>
  );
};
